import './css/App.scss'
import './utils/polyfills'
import * as dotPM from './utils/dotPostMessage'
import {
  convertLegacyLoadingIntoLoading,
  convertLegacyLoadToOnload,
  getEmbedHtml,
  getUrlParameter,
  loadQueue,
} from './utils/misc'
import { Page } from './Page'
import { PagePostMessager } from './utils/PagePostMessager'

const dummy = () => {}

/**
 * @typedef {Object<string, any>} URLData
 */

/**
 * @typedef {string} Datastore
 */

/**
 * @typedef {'yes'|'no'} YesNo
 */

/**
 * @typedef {'on'|'off'} OnOff
 */

/**
 * @typedef {Object} LoadingColor
 * @property {string} color
 */

/**
 * @typedef {OnOff=} LegacyLoading
 */

/**
 * @typedef {LoadingColor=} Loading
 */

/**
 * @typedef {Object} EmbedOptions
 * @property {string} dotId
 * @property {string} pageId
 * @property {string=} customdomainlink custom domain of project
 * @property {string=} title
 * @property {string=} dotExt dot extension
 * @property {string=} pageExt page extension
 * @property {URLData=} urlData
 * @property {Datastore=} datastore
 * @property {YesNo|boolean=} gaCrossDomainTracking
 * @property {string=} dotDomain which dot.vu domain to use (default: https://dot.vu/)
 */

/**
 * @typedef {Object} LoadStyleOptions
 * @property {string=} height embed height (default: 'auto')
 * @property {string=} width embed width (default: 'auto')
 * @property {string=} ratio embed ratio
 */

/**
 * @typedef {Object} LoadingOptions
 * @property {Loading|OnOff} loading create loading animation (default: 'on')
 */

/** @typedef {EmbedOptions & LoadStyleOptions & LoadingOptions} LoadOptions */

/**
 *
 * @param {HTMLElement} rootElement
 * @param {LoadOptions} argument
 * @param {(page: Page) => any} cb
 */
const load = function (rootElement, argument, cb = dummy) {
  if (!(argument !== null && typeof argument === 'object')) {
    throw new Error('dot-embed: Invalid iframe argument. Expected object.')
  }
  if (!argument.hasOwnProperty('dotId') || !argument.hasOwnProperty('pageId')) {
    throw new Error('dot-embed: Invalid arguments. Expected dotId and pageId.')
  }

  var loading = convertLegacyLoadingIntoLoading(
    argument.loading,
    argument.loadingColor
  )

  // get IDs
  var dotId = argument.dotId
  var pageId = argument.pageId
  if (!dotId || !pageId) {
    throw new Error('dot-embed: Invalid arguments. Expected dotId and pageId')
  }

  // datastore
  // Convert a query string field into a datastore url
  const datastoreValue = getUrlParameter(argument.datastore)
  const datastorePath =
    argument.datastore && datastoreValue ? '/d/' + datastoreValue : ''

  // get extensions
  var urlDataObj = argument.urlData || {}
  var urlData = []
  for (var prop in urlDataObj) {
    if (Object.prototype.hasOwnProperty.call(urlDataObj, prop)) {
      urlData.push(prop + '=' + encodeURIComponent(urlDataObj[prop]))
    }
  }
  var dotExt = argument.dotExt || '_' + dotId
  var pageExt = argument.pageExt || '_' + pageId
  var title = argument.title || null

  var dotDomain = argument.dotDomain || 'https://dot.vu'

  var pageUrl = argument.customdomainlink
    ? argument.customdomainlink
    : dotDomain + '/p/' + dotExt + '/' + pageExt

  // Add Datastore
  pageUrl += datastorePath

  // Add URL Data
  pageUrl += urlData.length > 0 ? '?' + urlData.join('&') : ''

  // get options
  var gaCrossDomainTracking = argument.gaCrossDomainTracking

  // append iframe wrapping html including loading
  rootElement.appendChild(getEmbedHtml(loading))
  // loading div to hide on load
  var _loading = loading
    ? rootElement.getElementsByClassName('dot-embed-loading')[0]
    : false
  // iframe container
  var iframeContainer = rootElement.getElementsByClassName(
    'dot-embed-iframe-container'
  )[0]
  // get other data
  var height = argument.height
  var width = argument.width
  var ratio = argument.ratio || false
  var dynamicHeight = height === 'auto' && !ratio
  // if a ratio is set, height is ignored
  if (ratio) {
    height = 'auto'
  }
  var hasUnits = function (value) {
    return parseFloat(value) + '' !== value + ''
  }
  // build iframe
  var iframe = document.createElement('iframe')
  iframe.setAttribute('frameborder', 0)
  iframe.setAttribute('width', width)
  iframe.setAttribute('height', height)
  if (title) iframe.setAttribute('title', title)
  if (!hasUnits(width)) width += 'px'
  if (!hasUnits(height)) height += 'px'
  iframe.style.width = width
  iframe.style.height = height
  var _iframeAllow = ['camera', 'autoplay', 'clipboard-write']
  iframe.setAttribute('allow', _iframeAllow.join('; '))
  // set no scrolling in iframe if dynamicHeight
  if (dynamicHeight) iframe.setAttribute('scrolling', 'no')
  // on ready
  var onReady = function () {
    dotPM.onMessage(dotId, pageId, 'popup_open', function (data) {
      iframe.scrollIntoView()
    })
    if (_loading) _loading.style.display = 'none'
    iframeContainer.style.display = 'block'
    iframeContainer.className += ' active'
    if (dynamicHeight) {
      dotPM.onMessage(dotId, pageId, 'height_update', function (data) {
        if (data.hasOwnProperty('height')) {
          iframeContainer.style.height = data.height + 'px'
          iframe.style.height = data.height + 'px'
          iframe.setAttribute('height', data.height)
        }
      })
      var lastRequest = 0
      var requestResize = function () {
        var newRequest = Date.now()
        if (lastRequest + 1000 < newRequest) {
          dotPM.sendMessage(
            dotId,
            pageId,
            'request_height',
            '*',
            {},
            iframe.contentWindow || iframe.contentDocument
          )
          lastRequest = newRequest
        }
      }
      var requestResizeIteration = function () {
        requestResize()
        setTimeout(requestResizeIteration, 1000)
      }
      window.addEventListener('resize', requestResize)
      requestResizeIteration()
    }

    return cb(new Page(new PagePostMessager({ iframe, dotId, pageId })))
  }

  if (!loading) {
    // load iframe to DOM
    iframeContainer.innerHTML = ''
    iframeContainer.appendChild(iframe)
    iframeContainer.style.display = 'block'
    iframeContainer.className += ' active'
    if (Object.prototype.hasOwnProperty.call(argument, 'noWaitForLoaded'))
      onReady()
    else dotPM.onMessage(dotId, pageId, 'load_finished', onReady)
  } else {
    dotPM.onMessage(dotId, pageId, 'load_finished', onReady)
    // init loading state
    iframeContainer.style.display = 'none'
    // load iframe to DOM
    iframeContainer.innerHTML = ''
    iframeContainer.appendChild(iframe)
  }

  var updateIframeUrl = function (_pageUrl) {
    iframe.setAttribute('src', _pageUrl)
  }

  if (gaCrossDomainTracking === 'yes' || gaCrossDomainTracking === true) {
    var maxTries = 40,
      wait = 200
    function whenGALoaded() {
      if (maxTries > 0) {
        try {
          if (
            window.GoogleAnalyticsObject &&
            window[window.GoogleAnalyticsObject]
          ) {
            var gobj = window[window.GoogleAnalyticsObject]
            var tracker = gobj.getAll()[0]
            var linker = new window.gaplugins.Linker(tracker)
            pageUrl = linker.decorate(pageUrl)
            updateIframeUrl(pageUrl)
          } else {
            maxTries--
            setTimeout(whenGALoaded, wait)
          }
        } catch (e) {
          maxTries--
          setTimeout(whenGALoaded, wait)
        }
      } else {
        console.warn(
          'DotEmbedLibrary: Gave up waiting for GA. Loading without linker.'
        )
        updateIframeUrl(pageUrl)
      }
    }
    whenGALoaded()
  } else updateIframeUrl(pageUrl)
}

const init = () => {
  // Call Queue
  const onLoadQ = window.dotEmbed?.onLoad?.q || []
  const loadQ = window.dotEmbed?.load?.q || []

  setTimeout(() => {
    loadQueue([].concat(onLoadQ, loadQ.map(convertLegacyLoadToOnload)))
  })
}

function onLoad(cb) {
  cb(window.dotEmbed)
}

export { load, init, onLoad }
